import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchContentLibraryApi,
  postContentLibraryApi,
  deleteContentLibraryApi,
} from "../../api/contentLibraryApi";

// Async thunk for fetching content library data
export const fetchContentLibraryList = createAsyncThunk(
  "fetchContentLibraryList",
  async ({ coachId }, { rejectWithValue }) => {
    try {
      return await fetchContentLibraryApi(coachId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error?.data?.title || "An error occurred"
      );
    }
  }
);

// Async thunk for creating new content
export const createContentLibrary = createAsyncThunk(
  "createContentLibrary",
  async (payload, { rejectWithValue }) => {
    try {
      return await postContentLibraryApi(payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Async thunk for deleting content (updated to refresh data)
export const deleteContentLibrary = createAsyncThunk(
  "deleteContentLibrary",
  async ({ id, coachId }, { rejectWithValue, dispatch }) => {
    try {
      // First delete the record
      const response = await deleteContentLibraryApi([`${id}`]);

      // Then refresh the content library
      await dispatch(fetchContentLibraryList({ coachId }));

      return {
        deletedId: id,
        message: response?.message || "Content deleted successfully",
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

const contentLibrarySlice = createSlice({
  name: "contentLibrary",
  initialState: {
    contentLibrary: [],
    isLoading: false,
    error: null,
    updateSuccess: false,
    updateError: false,
    deleteSuccess: false,
    deleteError: false,
    lastDeleteMessage: null,
  },
  reducers: {
    resetContentLibraryState: (state) => {
      state.contentLibrary = [];
      state.isLoading = false;
      state.error = null;
      state.updateSuccess = false;
      state.updateError = false;
      state.deleteSuccess = false;
      state.deleteError = false;
      state.lastDeleteMessage = null;
    },
    // Add this new action to reset only delete states
    resetDeleteState: (state) => {
      state.deleteSuccess = false;
      state.deleteError = false;
      state.lastDeleteMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Content Library Cases
      .addCase(fetchContentLibraryList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContentLibraryList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contentLibrary = action.payload;
      })
      .addCase(fetchContentLibraryList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create Content Library Cases
      .addCase(createContentLibrary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.updateSuccess = false;
        state.updateError = false;
      })
      .addCase(createContentLibrary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contentLibrary = action.payload;
        state.updateSuccess = true;
        state.updateError = false;
      })
      .addCase(createContentLibrary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.updateSuccess = false;
        state.updateError = true;
      })

      // Delete Content Library Cases (updated)
      .addCase(deleteContentLibrary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.deleteSuccess = false;
        state.deleteError = false;
        state.lastDeleteMessage = null;
      })
      .addCase(deleteContentLibrary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deleteSuccess = true;
        state.deleteError = false;
        state.lastDeleteMessage = action.payload.message;
      })
      .addCase(deleteContentLibrary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.deleteSuccess = false;
        state.deleteError = true;
        state.lastDeleteMessage = action.payload?.message || "Delete failed";
      });
  },
});

export const { resetContentLibraryState, resetDeleteState } = contentLibrarySlice.actions;

export const contentLibraryReducer = contentLibrarySlice.reducer;
